import { z } from 'zod';

/**
 * Canonical proactive-notification vocabulary.
 *
 * Members exist by construction (D-02): we only ship notification types that have
 * a live producer + consumer. `flight_schedule_change` is the involuntary
 * schedule-change driver. New members are added when a real sender needs them —
 * never pre-created "for future use".
 *
 * `voucher_created`, `trip_approved`, `trip_rejected`, `trip_ready` and
 * `trip_updated` are retired (#3765): see `RETIRED_NOTIFICATION_TYPES`.
 *
 * `approval_requested` is the approval-agent milestone driver: its producer (the
 * `approval-events` Kafka integrator) and consumer (this conversation redirect
 * path + the seed-message-types templates) ship in the SAME milestone (Phase 2) —
 * not speculative.
 *
 * `approval_trip_requested` is the deterministic, trip-first approval driver. It
 * replaces the voucher-first push producer for Slack: the source batches
 * pending vouchers per approver/trip, snapshots the actionable voucher ids, and
 * sends a single trip-context notification.
 *
 * `phone_verification_otp` is the phone-otp-verification milestone driver: it
 * carries the one-time code that proves the traveler owns the WhatsApp number
 * typed in the travel-preferences onboarding. Producer (customer service via the
 * whatsapp-integration sync send endpoint) and consumer (Meta) ship in the SAME
 * milestone — not speculative. Sent synchronously so the send result doubles as
 * the "is this a WhatsApp number" check.
 *
 * `flight_reminder` is the phase-4 flight-alert-evolution driver: the cirium
 * worker's own clock (not an airline event) fires it ahead of departure.
 * Producer (cirium worker) and consumer (conversation inbox + Slack/WhatsApp
 * copy) ship in the SAME milestone — not speculative. Purely informational (no
 * satellite valence, no CTA beyond "track the flight"), which is why it is
 * placed right after `flight_disruption` in the roster.
 *
 * `trip_update` (#3765): uma mensagem por viagem com o desfecho final de cada
 * item; substitui voucher_created/trip_ready/trip_updated/trip_approved/trip_rejected
 * como proativo.
 *
 * `flight_rebooked`: a back office do legado remarcou um voo (`FlightRebookedMetadataSchema`);
 * publicado pelo voucher-events. Usa a linha `trip_update` da grade de preferências.
 */
export const NotificationTypeSchema = z.enum([
  'flight_schedule_change',
  'voucher_created',
  'approval_requested',
  'approval_trip_requested',
  'flight_disruption',
  'flight_reminder',
  'phone_verification_otp',
  'trip_approved',
  'trip_rejected',
  'trip_ready',
  'trip_updated',
  'trip_update',
  'flight_rebooked',
  /** O "pedido recebido" do solicitante (Projeto B1): os vouchers que ele pediu numa reserva,
   *  agrupados em 5 min pelo voucher-events (`RequestReceivedMetadataSchema`). Sem WhatsApp no
   *  B1 (o modelo da Meta não foi aprovado): o conversation não tem linha de WhatsApp para ele. */
  'request_received',
  /** O convite (ou lembrete) do viajante para confirmar presença num evento de "Grupos e
   *  eventos" (`EventInviteMetadataSchema`), publicado pelo mice. Fora da grade de
   *  preferências: o produtor carimba `preferredChannel: 'email'`. Sem WhatsApp até o modelo
   *  da Meta existir. */
  'event_invite',
  /** Compra adicional do bilhete (assento ou bagagem extra): emitida pela companhia,
   *  cancelada (recusa, expiração ou cancelamento) ou não confirmada, com o nosso time
   *  acionado. TRÊS tipos pelo mesmo motivo de `trip_approved`/`trip_rejected`: o selo e o
   *  título mudam por desfecho. Nascem da rota interna de aviso dos ancilares do conversation
   *  (sem conversa, #4107), não de evento de sistema. */
  'ancillary_confirmed',
  'ancillary_canceled',
  'ancillary_failed',
  /** O agente chamou ao app uma conversa de outro canal (WhatsApp, Slack…) que ainda não
   *  migrou (#4057): um item no SINO do viajante, com "Continuar conversa" abrindo a conversa
   *  — é essa abertura que migra. Só existe no app: nasce do `_channel_switch` do
   *  conversation, nunca de evento de sistema, e nunca volta ao canal de origem (fora de
   *  `ProactiveNotificationType`). */
  'channel_switch_invite',
  /** O link de uma corrida 99 liberada pelo gestor do cliente num lote (mobility-batch-api).
   *  Envio síncrono pelo `/internal/send-template`, só WhatsApp: o resultado vira a situação do
   *  link (`sent`/`failed`). Fora de `ProactiveNotificationType`, como o OTP. */
  'mobility99_ride_link',
  /** O mesmo link no modo aberto (sem login): outro template da Meta, com o botão no host do
   *  web-chat. Síncrono, só WhatsApp, fora do fan-out, com as variáveis do `mobility99_ride_link`. */
  'mobility99_ride_link_public',
]);

export type NotificationType = z.infer<typeof NotificationTypeSchema>;

/**
 * Retired by #3765: kept in the enum because inbox history is validated against it;
 * no producer publishes them and no channel renders them. `trip_update` replaced all
 * five as the proactive trip message.
 */
export const RETIRED_NOTIFICATION_TYPES = [
  'voucher_created',
  'trip_ready',
  'trip_updated',
  'trip_approved',
  'trip_rejected',
] as const;

/**
 * The PROACTIVE, channel-fanned-out notification types — every channel integrator
 * (slack/teams/whatsapp) renders per-channel copy for these. EXCLUDES transactional
 * sync template sends like `phone_verification_otp`, which is WhatsApp-only,
 * Meta-template-rendered, and never fanned out for per-channel copy, the app-only
 * bell item `channel_switch_invite` (#4057), which no channel ever renders, the
 * WhatsApp-only sync ride links `mobility99_ride_link` and `mobility99_ride_link_public`, and the
 * `RETIRED_NOTIFICATION_TYPES`. Channel copy maps key off this (not the full enum) so
 * a transactional or retired type does not force a (meaningless) copy entry in every
 * channel.
 */
export const ProactiveNotificationTypeSchema = NotificationTypeSchema.exclude([
  'phone_verification_otp',
  'channel_switch_invite',
  'mobility99_ride_link',
  'mobility99_ride_link_public',
  ...RETIRED_NOTIFICATION_TYPES,
]);
export type ProactiveNotificationType = z.infer<typeof ProactiveNotificationTypeSchema>;

/** As variáveis dos dois templates do link de corrida em lote (com login e aberto). */
const RIDE_LINK_VARS = [
  'firstName',
  'clientName',
  'batchName',
  'origin',
  'destination',
  'validity',
] as const;

/**
 * Named template variables per notification type (D-03).
 *
 * The keys cover the example copy: "monitorando voucher {{voucherCode}},
 * identificamos {{changeSummary}}". `voucherCode` is the VOLL voucher code
 * (resolved by-locator) shown to the traveler — NOT the supplier flight locator
 * (PNR), which is an internal routing key. The consumer (whatsapp-integration
 * registry) reads `paramOrder` from this map so template params stay named, never
 * positional magic.
 */
export const NOTIFICATION_VARS = {
  flight_schedule_change: ['voucherCode', 'changeSummary'],
  // The RETIRED_NOTIFICATION_TYPES (#3765) keep their lists only because the enum
  // keeps them: no producer publishes them and no channel renders them.
  voucher_created: ['voucherCode', 'modalLabel', 'travelSummary', 'periodSummary'],
  // approval_requested: the proactive "voucher awaiting your approval" notification
  // to each approver. Kept as separate display facts so channel templates can render
  // a scannable "Voucher / Traveler / Modal / Company / Cost center" layout.
  approval_requested: [
    'voucherCode',
    'travelerName',
    'modalLabel',
    'companyName',
    'costCenterName',
  ],
  // approval_trip_requested: the proactive "trip has pending approvals" notification
  // to one approver. `batchId` is opaque and used by interactive channels to bind
  // a button click to the server-side snapshot; the other values are display-only.
  approval_trip_requested: [
    'batchId',
    'tripTitle',
    'travelerName',
    'pendingCount',
    'totalCount',
    'pendingSummary',
    'contextSummary',
  ],
  // flight_disruption: the proactive "your flight changed" alert from the cirium
  // disruption monitor. Two vars = the two message lines, one per body param of
  // the Meta template `voll_flight_alert` (#3761). `vars.actionable` still rides
  // along for the other channels' CTA. The conversation guarantees both lines (the flight line
  // falls back to the voucher label). `voucherCode`, `actionable`, `ctaLine` and
  // the legacy `alertSummary` still ride in `vars` for inbox/e-mail/Slack and for
  // pre-2b integrator builds, but are NOT part of this contract.
  flight_disruption: ['headline', 'flightLine'],
  // flight_reminder: the pre-flight reminder from the cirium worker's own clock.
  // Two vars = the two message lines, one per body param of the Meta template
  // `voll_flight_reminder_v2` (both always present: the headline is composed from
  // the leg identity, the flight line from the snapshot state with fallbacks).
  flight_reminder: ['headline', 'flightLine'],
  // phone_verification_otp: the one-time code sent to the WhatsApp number the
  // traveler typed in onboarding. Single body var — the Meta template is a
  // UTILITY body-only message ("Seu código VOLL é {{otpCode}}"). No buttons (a
  // copy-code button would make it an AUTHENTICATION-category template).
  phone_verification_otp: ['otpCode'],
  // Retired (#3765), like voucher_created above.
  trip_approved: ['tripTitle', 'deciderName'],
  trip_rejected: ['tripTitle', 'deciderName'],
  trip_ready: ['destination', 'periodSummary', 'itinerarySummary', 'voucherCodes'],
  // ancillary_confirmed: o que foi confirmado ("assento 5B na ida (CGH → SDU)"), o voucher
  // aéreo pai e o valor que o legado gravou, quando veio. Cancelado e falha levam só o
  // pedido e o voucher: não há valor cobrado.
  ancillary_confirmed: ['voucherCode', 'summary', 'amount'],
  ancillary_canceled: ['voucherCode', 'summary'],
  ancillary_failed: ['voucherCode', 'summary'],
  // Retired (#3765), like voucher_created above.
  trip_updated: ['destination', 'changeSummary', 'itinerarySummary', 'voucherCodes'],
  // trip_update (#3765): the Meta `voll_trip_update_v2` body order. Other vars ride
  // along unpositioned: `tripPath` (URL button), `tripUrl`, `itemsJson`,
  // `itemList`, `situation` (Slack, e-mail, inbox).
  trip_update: ['destination', 'period', 'summary', 'items'],
  // flight_rebooked: the rebooked flight's identity, date and time; `headline`, `summary` and
  // `text` ride along unpositioned (inbox, e-mail, chat). No Meta template yet.
  flight_rebooked: ['flight', 'date', 'time'],
  // request_received (Projeto B1): two lines for a future Meta template. The
  // conversation also sends, unpositioned: `itemList` (one "• " line per item),
  // `tripTitle`, `approvalLine` (only when an item waits for approval), `tripPath`
  // and `tripUrl` (only with the requester's own trip).
  request_received: ['headline', 'items'],
  // event_invite: the parameter order of a future Meta template. Unpositioned, the
  // conversation also sends `kind` ('invite' | 'reminder'), `title`, `venueName`
  // (only with a venue), `eventPath` and `eventUrl`.
  event_invite: ['travelerFirstName', 'companyName', 'eventName', 'eventDates', 'deadline'],
  // channel_switch_invite: só o sino do app (sem template de canal); o canal de origem
  // decide o título ("Seu atendimento do WhatsApp…" ou o genérico).
  channel_switch_invite: ['originChannel'],
  // mobility99_ride_link: o corpo de `voll_mobility99_ride_link` (e das versões _dev/_rls),
  // {{1}}..{{6}} nesta ordem. `linkToken` viaja fora da lista: é o sufixo do botão de URL. O
  // `_public` é o mesmo corpo com o botão no host do web-chat (link sem login).
  mobility99_ride_link: RIDE_LINK_VARS,
  mobility99_ride_link_public: RIDE_LINK_VARS,
} as const satisfies Record<NotificationType, readonly string[]>;
